import { BrandColors, Colors } from "@/constants/theme";
import { formatBytesToMB } from "@/lib/image-picker";
import { MAX_UPLOAD_SIZE } from "@/lib/validation/file";
import Ionicons from "@react-native-vector-icons/ionicons";
import React, { useCallback, useRef, useState } from "react";
import {
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { ThemedText } from "../themed-text";

export type UploadSource = "device" | "camera" | "library";

const isWeb = Platform.OS === "web";

const SOURCE_OPTIONS: {
  source: UploadSource;
  label: string;
  icon: "folder-outline" | "camera-outline" | "images-outline";
}[] = [
  { source: "device", label: "Choose File", icon: "folder-outline" },
  { source: "camera", label: "Camera", icon: "camera-outline" },
  { source: "library", label: "Photo Library", icon: "images-outline" },
];

export function UploadSourceModal({
  visible,
  title,
  onClose,
  onSelect,
  onDropFile,
}: {
  visible: boolean;
  title: string;
  onClose: () => void;
  onSelect: (source: UploadSource) => void;
  onDropFile?: (file: File) => void;
}) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const isDark = colorScheme === "dark";
  const pendingSource = useRef<UploadSource | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const options = isWeb
    ? SOURCE_OPTIONS.filter((option) => option.source === "device")
    : SOURCE_OPTIONS;

  /**
   * iOS cannot present the camera or library while this modal is still
   * dismissing, so the selection is deferred until `onDismiss`.
   */
  const handleSelect = (source: UploadSource) => {
    if (Platform.OS === "ios") {
      pendingSource.current = source;
      onClose();
      return;
    }

    onSelect(source);
    onClose();
  };

  const handleDismiss = () => {
    const source = pendingSource.current;
    pendingSource.current = null;
    if (source) {
      onSelect(source);
    }
  };

  const dropZoneRef = useCallback(
    (node: View | null) => {
      if (!isWeb || !node || !onDropFile) {
        return;
      }

      const element = node as unknown as HTMLElement;
      const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
        setIsDragging(true);
      };
      const handleDragLeave = () => setIsDragging(false);
      const handleDrop = (event: DragEvent) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer?.files[0];
        if (file) {
          onDropFile(file);
          onClose();
        }
      };

      element.addEventListener("dragover", handleDragOver);
      element.addEventListener("dragleave", handleDragLeave);
      element.addEventListener("drop", handleDrop);

      return () => {
        element.removeEventListener("dragover", handleDragOver);
        element.removeEventListener("dragleave", handleDragLeave);
        element.removeEventListener("drop", handleDrop);
      };
    },
    [onClose, onDropFile],
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      onDismiss={handleDismiss}
    >
      <View style={styles.overlay}>
        <View style={[styles.content, { backgroundColor: theme.background }]}>
          <ThemedText type="defaultSemiBold" style={styles.title}>
            {title}
          </ThemedText>

          <TouchableOpacity
            ref={dropZoneRef}
            testID="upload_source_drop_zone"
            style={[
              styles.dropZone,
              {
                backgroundColor: isDark ? theme.card : BrandColors.primaryLight,
                borderColor: isDragging ? BrandColors.primary : theme.border,
              },
            ]}
            onPress={() => handleSelect("device")}
          >
            <Ionicons name="image" size={56} color={BrandColors.primary} />
            <ThemedText type="defaultSemiBold">
              {isWeb ? "Drop files here" : "Tap to browse files"}
            </ThemedText>
            <ThemedText type="extraSmall" style={styles.hint}>
              Image or PDF · Max {formatBytesToMB(MAX_UPLOAD_SIZE, 0)}
            </ThemedText>
          </TouchableOpacity>

          <View>
            {options.map((option) => (
              <TouchableOpacity
                key={option.source}
                testID={`upload_source_${option.source}`}
                style={styles.option}
                onPress={() => handleSelect(option.source)}
              >
                <Ionicons name={option.icon} size={22} color={theme.text} />
                <ThemedText>{option.label}</ThemedText>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            testID="upload_source_cancel"
            style={[
              styles.cancelButton,
              {
                backgroundColor: isDark
                  ? theme.card
                  : BrandColors.backgroundGray,
              },
            ]}
            onPress={onClose}
          >
            <ThemedText>Cancel</ThemedText>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  content: {
    width: "100%",
    maxWidth: 400,
    borderRadius: 16,
    padding: 16,
    gap: 12,
  },
  title: {
    textAlign: "center",
  },
  dropZone: {
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 32,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
  },
  hint: {
    color: BrandColors.mediumGray,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    paddingVertical: 14,
    paddingHorizontal: 12,
  },
  cancelButton: {
    minHeight: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
});
